import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, FileText, User, Calendar, Download } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import hrDocumentApprovalService from '../../../services/hrDocumentApprovalService';
import { useAuth } from '../../../contexts/AuthContext';

const DocumentReviewModal = ({ document, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [action, setAction] = useState(null); // 'approve' or 'reject'

  // Form state
  const [formData, setFormData] = useState({
    comments: '',
    rejectionReason: '',
    signatureName: '',
    signatureTitle: 'HR Manager'
  });

  useEffect(() => {
    loadPreview();
  }, [document]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError('');
      const url = await hrDocumentApprovalService?.getDocumentUrl(document?.storagePath);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load document preview');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!formData?.signatureName?.trim()) {
      setError('Please enter your name for the signature');
      return;
    }

    if (!formData?.comments?.trim()) {
      setError('Please provide approval comments');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      await hrDocumentApprovalService?.approveDocument(
        document?.id,
        user?.email,
        formData?.comments,
        formData?.signatureName,
        formData?.signatureTitle
      );

      onSuccess();
    } catch (err) {
      console.error('Error approving document:', err);
      setError(err?.message || 'Failed to approve document');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!formData?.rejectionReason?.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      await hrDocumentApprovalService?.rejectDocument(
        document?.id,
        user?.email,
        formData?.rejectionReason
      );

      onSuccess();
    } catch (err) {
      console.error('Error rejecting document:', err);
      setError(err?.message || 'Failed to reject document');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024)?.toFixed(2)} MB`;
  };

  const canPreview = document?.mimeType?.startsWith('image/') || document?.mimeType === 'application/pdf';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 truncate">{document?.documentName}</h2>
            <p className="text-sm text-gray-600 mt-1">{document?.fileName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start gap-2">
            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex">
          {/* Preview Area */}
          <div className="flex-1 bg-gray-100 p-6 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading preview...</p>
                </div>
              </div>
            ) : !canPreview ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                  <Button
                    onClick={() => window.open(previewUrl, '_blank')}
                    iconName="Download"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Download to View
                  </Button>
                </div>
              </div>
            ) : document?.mimeType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-lg border border-gray-300"
                title="Document Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <img
                  src={previewUrl}
                  alt={document?.documentName}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            )}
          </div>

          {/* Review Panel */}
          <div className="w-96 bg-white border-l border-gray-200 p-6 overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Details</h3>

            <div className="space-y-4 mb-6">
              {/* Employee */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <User className="w-4 h-4" />
                  <span>Employee</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{document?.employeeName}</p>
                <p className="text-xs text-gray-600">{document?.employeeNumber}</p>
                <p className="text-xs text-gray-600">{document?.department}</p>
              </div>

              {/* Document Date */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Document Date</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{formatDate(document?.documentDate)}</p>
              </div>

              {/* File Info */}
              <div>
                <div className="text-sm text-gray-500 mb-1">File Information</div>
                <div className="text-sm space-y-1">
                  <p className="text-gray-900">
                    <span className="font-medium">Size:</span> {formatFileSize(document?.fileSizeBytes)}
                  </p>
                  <p className="text-gray-900">
                    <span className="font-medium">Type:</span> {document?.mimeType}
                  </p>
                </div>
              </div>

              {/* Description */}
              {document?.description && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Description</div>
                  <p className="text-sm text-gray-900">{document?.description}</p>
                </div>
              )}
            </div>

            {/* Action Selection */}
            {!action && (
              <div className="space-y-3">
                <Button
                  onClick={() => setAction('approve')}
                  iconName="CheckCircle"
                  fullWidth
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Approve Document
                </Button>
                <Button
                  onClick={() => setAction('reject')}
                  iconName="XCircle"
                  fullWidth
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  Reject Document
                </Button>
              </div>
            )}

            {/* Approval Form */}
            {action === 'approve' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-700 mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Approve Document</span>
                </div>

                <Input
                  label="Your Name (for signature)"
                  required
                  value={formData?.signatureName}
                  onChange={(e) => setFormData(prev => ({ ...prev, signatureName: e?.target?.value }))}
                  placeholder="Enter your full name"
                />

                <Input
                  label="Your Title"
                  required
                  value={formData?.signatureTitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, signatureTitle: e?.target?.value }))}
                  placeholder="e.g., HR Manager"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Comments <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData?.comments}
                    onChange={(e) => setFormData(prev => ({ ...prev, comments: e?.target?.value }))}
                    placeholder="Enter approval comments..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleApprove}
                    disabled={processing}
                    iconName="CheckCircle"
                    fullWidth
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {processing ? 'Processing...' : 'Approve'}
                  </Button>
                  <Button
                    onClick={() => setAction(null)}
                    disabled={processing}
                    variant="outline"
                    fullWidth
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Rejection Form */}
            {action === 'reject' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-red-700 mb-4">
                  <XCircle className="w-5 h-5" />
                  <span className="font-semibold">Reject Document</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData?.rejectionReason}
                    onChange={(e) => setFormData(prev => ({ ...prev, rejectionReason: e?.target?.value }))}
                    placeholder="Provide a clear reason for rejection..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleReject}
                    disabled={processing}
                    iconName="XCircle"
                    fullWidth
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {processing ? 'Processing...' : 'Reject'}
                  </Button>
                  <Button
                    onClick={() => setAction(null)}
                    disabled={processing}
                    variant="outline"
                    fullWidth
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentReviewModal;